import { RightOutlineIcon } from '@carboncopyco/icons';
import { Button } from '@carboncopyco/ui';
import type { ReactNode } from 'react';
import { FixedTable, type FixedTableProps } from '../../FixedTable';
import { RowWrapper } from '../RowWrapper';
import { TableErrorState } from '../SessionBreakdownComponents';
import { MinimalTableSkeleton } from './MinimalTableSkeleton';

type MinimalTableProps<T> = {
  cols: FixedTableProps<T>['cols'];
  data: FixedTableProps<T>['data'];
  isLoading: FixedTableProps<T>['isLoading'];
  header: ReactNode;
  onViewAll?: () => void;
  emptyState?: ReactNode;
  onRowClick?: (item: T) => void;
  error?: FixedTableProps<T>['error'];
  maxItems?: number;
};

export const MinimalTable = <T extends Record<string, any>>({
  cols,
  data,
  isLoading,
  header,
  onViewAll,
  emptyState,
  onRowClick,
  error,
  maxItems = 3,
}: MinimalTableProps<T>) => {
  return (
    <div className="flex flex-col rounded-lg border border-ds-natural-150 bg-ds-accent3-100">
      <div className="flex items-center pl-6 h-ds-15 border-b border-ds-natural-150">
        {header}
      </div>
      <div className="flex flex-col grow justify-between">
        {error ? (
          <TableErrorState />
        ) : data.length || isLoading ? (
          <>
            <div className="pt-4.5 pb-4 overflow-hidden">
              <FixedTable
                sideSpacing
                cols={cols}
                data={data.slice(0, maxItems)}
                isLoading={isLoading}
                loader={
                  <div className="px-6">
                    <MinimalTableSkeleton rows={maxItems} />
                  </div>
                }
                rowWrapper={({ children, item }) => (
                  <RowWrapper
                    {...(onRowClick && onRowClick(item) !== undefined
                      ? {
                          onRowClick: () => onRowClick(item),
                        }
                      : {})}
                  >
                    {children}
                  </RowWrapper>
                )}
                loaderPlaceholderSpace={0}
                headerRowClassName="px-6"
              />
            </div>
            {!isLoading && data.length > maxItems && (
              <div className="pb-4 pt-1 pl-6 [&>button]:p-0">
                <Button
                  blue
                  link
                  label="View all"
                  rightIcon={<RightOutlineIcon />}
                  onClick={onViewAll}
                />
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center w-full p-6 h-full">
            {emptyState}
          </div>
        )}
      </div>
    </div>
  );
};
